import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createCustomerComplaint, getCustomerComplaintsByCustomer, removeCustomerComplaint, updateCustomerComplaint } from "@/redux/actions/customerComplaintActions";
import { Table, Button, Modal, Form, DatePicker, Input, Popconfirm, Row, Col, Space, Select, Tag } from "antd";
import moment from "moment";
import { PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import { checkPermission } from "@/utils/permission";
import { isMobile } from "react-device-detect";
import HtmlParser from "react-html-parser";
import { _newLine } from "@/utils/helpers";
import BaseRadioList from "@/components/Elements/BaseRadios";
import { getCustomerServices } from '@/redux/actions/customerServicesActions';

const { TextArea } = Input;

const CustomerComplaintList = ({ customer }) => {
    const customerId = customer?.id;

    const dispatch = useDispatch();

    const { customerComplaintList } = useSelector(
        (state) => state.customerComplaintReducer
    );
    const customerServices = useSelector(state => state.customerServicesReducer.customer_services);

    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingItem, setEditingItem] = useState(null);

    const [form] = Form.useForm();

    const allowCreate = checkPermission("customer_complaints", "create");
    const allowUpdate = checkPermission("customer_complaints", "update");
    const allowDelete = checkPermission("customer_complaints", "remove");

    const loadData = async () => {
        if (!customerId) return;
        setLoading(true);

        await dispatch(getCustomerComplaintsByCustomer({}, customerId));

        setLoading(false);
    };

    useEffect(() => {
        loadData();
        if (customerId) dispatch(getCustomerServices(customerId));
    }, [customerId]);

    const handleCreate = () => {
        setEditingItem(null);
        form.resetFields();
        setModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingItem(record);

        form.setFieldsValue({
            // DatePicker của antd cần moment object
            reported_at: record.reported_at ? moment(record.reported_at) : null,
            content: _newLine(record.content) || "",
            handled_result: _newLine(record.handled_result) || "",
            status: record.status,
            customer_service_ids: (record.customer_services || []).map(item => item.id)
        });

        setModalVisible(true);
    };

    const handleSubmit = async () => {
        const values = await form.validateFields();

        const payload = {
            ...values,
            reported_at: values.reported_at
                ? values.reported_at.format("YYYY-MM-DD HH:mm:ss")
                : null
        };

        if (editingItem) {
            await dispatch(updateCustomerComplaint(customerId, editingItem.id, payload));
        } else {
            await dispatch(createCustomerComplaint(customerId, payload));
        }

        setModalVisible(false);
        loadData();
    };

    const handleDelete = async (id) => {
        await dispatch(removeCustomerComplaint([id]));
        loadData();
    };

    const columns = [
        {
            title: "Dịch vụ liên quan",
            dataIndex: "customer_services",
            width: 240,
            render: (items = []) => items.map(item => <Tag key={item.id}>{item.service?.name || `#${item.id}`}</Tag>)
        },
        {
            title: "STT",
            dataIndex: "stt",
            width: 50,
            align: "center",
            render: (text, record, index) => index + 1
        },
        {
            title: "Ngày phản ánh",
            key: "reported_at",
            width: 130,
            render: (_, record) => {
                if (!record.reported_at) return null;
                const date = moment(record.reported_at).format("YYYY-MM-DD");
                const time = moment(record.reported_at).format("HH:mm");
                return (
                    <div style={{ lineHeight: 1.25 }}>
                        <div>{date} <span style={{ color: "#999", fontSize: 12 }}>{time}</span></div>
                    </div>
                );
            }
        },
        {
            title: "Nội khách hàng phản ánh",
            dataIndex: "content",
            width: 350,
            render: (text) => <>{HtmlParser(text)}</>
        },
        {
            title: "Kết quả xử lý",
            dataIndex: "handled_result",
            render: (text) => <>{HtmlParser(text)}</>
        },
        {
            title: "Trạng thái",
            dataIndex: "status",
            width: 180,
            render: (_, record) => {
                const label =
                    record.status === "resolved" ? "Đã xử lý" : "Chưa xử lý";

                return (
                    <div style={{ lineHeight: 1.3 }}>
                        <span
                            style={{
                                padding: "2px 8px",
                                borderRadius: 4,
                                background: record.status === "resolved" ? "#E6FFFB" : "#FFF7E6",
                                color: record.status === "resolved" ? "#389E0D" : "#FA8C16",
                                fontWeight: 600,
                                fontSize: 13
                            }}
                        >
                            {label}
                        </span>

                        {record.status === "resolved" && record.handled_by && (
                            <div style={{ marginTop: 4, fontSize: 12, color: "#666" }}>
                                <div>
                                    <span style={{ color: "#999" }}>Bởi: </span>
                                    <b>{record.handled_by.full_name}</b> {" "}
                                    {record.handled_by.user_role?.name && (
                                        <span style={{ color: "#aaa" }}>
                                            ({record.handled_by.user_role.name})
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                );
            }
        },
        {
            title: "Hành động",
            width: 130,
            align: "center",
            render: (_, record) => (
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: 6
                    }}
                >
                    <Button
                        size="small"
                        type="primary"
                        disabled={!allowUpdate}
                        onClick={() => handleEdit(record)}
                    >
                        Cập nhật / Xử lý
                    </Button>

                    <Popconfirm
                        title="Bạn có chắc muốn xoá bản ghi này?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Xoá"
                        placement="left"
                        cancelText="Hủy"
                        disabled={!allowDelete}
                    >
                        <Button disabled={!allowDelete} danger type="link">
                            Xoá
                        </Button>
                    </Popconfirm>
                </div>
            )
        }
    ];

    return (
        <div>
            <div className="mt-4 mb-4">
                <Row gutter={8}>
                    <Col>
                        <Space size={8}>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={handleCreate}
                                disabled={!allowCreate}
                            >
                                Thêm mới
                            </Button>
                            <Button
                                onClick={loadData}
                                icon={<ReloadOutlined />}
                                loading={loading}
                            >
                                Refresh
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </div>

            <Table
                size="small"
                dataSource={customerComplaintList || []}
                columns={columns}
                rowKey="id"
                loading={loading}
                pagination={false}
                bordered
            />

            <Modal
                title={editingItem ? "Cập nhật bản ghi" : "Thêm bản ghi mới"}
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                onOk={handleSubmit}
                okText="Lưu"
                cancelText="Hủy"
                maskClosable={false}
                width={isMobile ? "100%" : "50%"}
            >
                <Form layout="vertical" form={form}>
                    <Form.Item
                        name="customer_service_ids"
                        label="Dịch vụ liên quan"
                        rules={[{ required: true, message: "Chọn ít nhất một dịch vụ liên quan" }]}
                    >
                        <Select mode="multiple" showSearch optionFilterProp="children" placeholder="Chọn dịch vụ phát sinh phản ánh">
                            {(customerServices?.options || []).map(item => <Select.Option key={item.id} value={item.id}>{item.label}</Select.Option>)}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="reported_at"
                        label="Ngày khách hàng phản ánh"
                        rules={[{ required: true, message: "Chọn ngày khách hàng phản ánh" }]}
                    >
                        <DatePicker
                            showTime
                            style={{ width: "100%" }}
                            format="YYYY-MM-DD HH:mm"
                        />
                    </Form.Item>

                    <Form.Item
                        name="content"
                        label="Nội dung khách hàng phản ánh"
                        rules={[{ required: true, message: "Nhập nội dung khách hàng phản ánh" }]}
                    >
                        <TextArea rows={8} placeholder="Nhập nội dung khách hàng phản ánh..." />
                    </Form.Item>

                    <Form.Item
                        label="Trạng thái"
                        name="status"
                        rules={[{ required: true, message: "Chọn trạng thái" }]}
                    >
                        <BaseRadioList
                            options={[
                                { label: "Đã xử lý", value: 'resolved' },
                                { label: "Chưa xử lý", value: 'pending' }
                            ]}
                        />
                    </Form.Item>

                    <Form.Item noStyle shouldUpdate>
                        {({ getFieldValue }) => {
                            const status = getFieldValue("status");
                            return status === "resolved" ? (
                                <Form.Item
                                    name="handled_result"
                                    label="Kết quả xử lý"
                                    rules={[{ required: true, message: "Nhập kết quả xử lý" }]}
                                >
                                    <TextArea
                                        rows={8}
                                        placeholder="Nhập nội dung kết quả xử lý..."
                                    />
                                </Form.Item>
                            ) : null;
                        }}
                    </Form.Item>

                </Form>
            </Modal>

        </div>
    );
};

export default CustomerComplaintList;
